import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import useApi from '../hooks/useApi';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import Pagination from '../components/common/Pagination';
import toast from 'react-hot-toast';
import { Button } from '../styles/common/Button.styled';
import { ConsoleInput, ConsoleSelect } from '../styles/common/input.styled';
import { StatusTone } from '../styles/consoleTheme';
import { getLocalDateInputValue } from '../utils/date';
import * as T from '../styles/common/table.styled';

type MobileCallLog = {
  id: number;
  userId: number;
  source: string;
  category: string;
  eventName: string;
  callId?: string;
  clientCallId?: string;
  clientEventId?: string;
  occurredAt?: string;
  receivedAt: string;
  deliveryStatus?: string;
  message?: string;
  details?: string;
};

type PageData = { content?: MobileCallLog[]; totalPages?: number; totalElements?: number };
type Filters = Record<'userId' | 'callId' | 'clientCallId' | 'eventName' | 'category' | 'source' | 'deliveryStatus' | 'receivedAtFrom' | 'receivedAtTo', string> & { limit: string };
type DetailsPreview = { id: number; text: string; left: number; top: number; width: number; maxHeight: number; above: boolean };
type TimeActions = { id: number; receivedAt: string; left: number; top: number; above: boolean; focusActions: boolean };

const FILTER_KEYS = ['userId', 'callId', 'clientCallId', 'eventName', 'category', 'source', 'deliveryStatus'] as const;
const CATEGORIES = ['CALL_FLOW', 'PUSH', 'NETWORK', 'MEDIA', 'ERROR'];
const CATEGORY_TONES: Record<string, StatusTone> = { CALL_FLOW: 'info', PUSH: 'warning', MEDIA: 'success', ERROR: 'danger' };
const NETWORK_COLORS = { light: '#6941C6', dark: '#C4B5FD' };
const SOURCES = ['MOBILE_CLIENT', 'FCM', 'PUSHKIT'];
const DELIVERY_STATUSES = ['QUEUED', 'ACCEPTED', 'FAILED', 'EXPIRED', 'DISPATCH_REJECTED'];
const EVENT_LABELS: Record<string, string> = {
  push_received: 'Push 수신', push_expired: '수신 Push 만료', prewarm_notification: '사전 통화 알림 표시',
  runtime_ready: '런타임 준비 완료', native_event_replayed: '네이티브 이벤트 전달', native_call_reported: 'OS 통화 표시 보고',
  incoming_call: '수신 통화 Push', outgoing_requested: '발신 요청', sip_registration: 'SIP 등록 상태',
  notify_register: '수신 등록 요청', sip_invite: 'SIP INVITE 관측', call_state: '통화 상태',
  native_call_rejected: '네이티브 통화 거절 요청', native_call_ended: '네이티브 통화 종료 요청',
  hangup_requested: '통화 종료 요청', call_terminated: 'SIP 세션 종료 결과',
  network_changed: '네트워크 변경', sip_transport: 'SIP 전송 상태', sip_recovery: 'SIP 복구 단계',
  media_capture: '마이크 획득 또는 해제', sdp_negotiation: 'SDP 협상 관측', ice_state: 'ICE 상태',
  media_stream: '미디어 스트림 연결', rtp_summary: 'RTP 통계', audio_route: '오디오 경로 변경',
  runtime_start_failed: '런타임 시작 실패', prewarm_notification_failed: '사전 통화 알림 표시 실패',
  native_call_report_failed: 'OS 통화 보고 실패', incoming_bootstrap_failed: '수신 초기화 실패',
  notify_register_failed: '수신 등록 요청 실패', sip_registration_failed: 'SIP 등록 실패',
  sip_recovery_failed: 'SIP 복구 실패', call_failure: '통화 처리 실패', media_capture_failed: '마이크 획득 실패',
  sdp_negotiation_failed: 'SDP 협상 실패', ice_failed: 'ICE 연결 실패', audio_route_failed: '오디오 경로 적용 실패',
  server_error: 'API 서버 오류', api_connection_failed: 'API 연결 실패',
};
const EVENT_OPTIONS = Object.entries(EVENT_LABELS).sort(([a], [b]) => a.localeCompare(b));
const isKnownEvent = (value: string) => Object.prototype.hasOwnProperty.call(EVENT_LABELS, value);

const PageContainer = T.PageContainer;
const FiltersPanel = styled.form`display: flex; flex-direction: column; gap: 12px; padding: 0 0 16px; border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};`;
const QuickFilters = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const FilterGrid = styled.div`display: flex; flex-wrap: wrap; gap: 10px;`;
const Field = styled.div<{ $width?: number }>`
  display: flex; flex-direction: column; gap: 5px;
  flex: ${({ $width }) => $width ? `0 0 ${$width}px` : '1 1 170px'};
  min-width: ${({ $width }) => $width ? `${$width}px` : '160px'};
`;
const Label = styled.label`color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px;`;
const FieldError = styled.span`color: ${({ theme }) => theme.color.point_n_100}; font-size: 12px;`;
const Input = styled(ConsoleInput)`width: 100%;`;
const Select = styled(ConsoleSelect)`width: 100%;`;
const EventControl = styled.div`
  display: flex; align-items: center; gap: 6px; min-width: 0;
  > input, > select { flex: 1; min-width: 0; width: 0; }
`;
const EventModeTabs = styled.div`display: flex; flex: none; gap: 2px; padding: 3px; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;`;
const EventModeButton = styled(Button).attrs({ $variant: 'ghost', $size: 'sm' })`
  padding: 0 6px; color: ${({ theme }) => theme.color.text_color_300};
  &[aria-pressed='true'] { background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_100}; }
`;
const CategoryBadge = styled(T.Badge)<{ $category: string }>`
  ${({ theme, $category }) => $category === 'NETWORK' && `
    color: ${NETWORK_COLORS[theme.name]};
    background: ${NETWORK_COLORS[theme.name]}18;
  `}
`;
const Actions = styled.div`display: flex; align-items: flex-end; gap: 8px;`;
const Note = styled.p`margin: 0; color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; line-height: 18px;`;
const Results = styled.div`display: flex; flex-direction: column; gap: 8px; min-width: 0;`;
const TableTools = styled.div`display: flex; justify-content: flex-end;`;
const TableArea = styled(T.TableArea)`& > table { min-width: 1370px; }`;
const Cell = styled(T.Td)`white-space: normal; overflow-wrap: anywhere;`;
const InlineDetails = styled.div`
  position: sticky; left: 0; z-index: 2; box-sizing: border-box;
  width: min(650px, calc(100vw - 300px)); max-width: calc(100vw - 300px);
  display: flex; flex-direction: column; gap: 8px; padding: 12px; white-space: normal;
  border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  background: ${({ theme }) => theme.color.primary_100};
`;
const DetailGrid = styled.dl`display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 4px 12px; margin: 0; font-size: 12px; dt { color: ${({ theme }) => theme.color.text_color_300}; } dd { min-width: 0; margin: 0; overflow-wrap: anywhere; }`;
const PrettyJson = styled.pre`
  max-width: 100%; margin: 0; padding: 12px; overflow: auto; border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.color.primary_300};
  background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_100};
  font: 12px/19px ui-monospace, SFMono-Regular, Menlo, monospace; white-space: pre; tab-size: 2;
`;
const DetailsTooltip = styled.pre`
  position: fixed; z-index: 500; box-sizing: border-box; margin: 0; padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  background: ${({ theme }) => theme.color.primary_100}; color: ${({ theme }) => theme.color.text_color_100};
  box-shadow: 0 8px 24px ${({ theme }) => theme.name === 'dark' ? '#00000070' : '#10182824'};
  font: 12px/19px ui-monospace, SFMono-Regular, Menlo, monospace; white-space: pre; overflow: hidden;
  pointer-events: none;
`;
const TimeTrigger = styled.button`
  padding: 0; border: 0; background: transparent; color: inherit; font: inherit;
  font-variant-numeric: tabular-nums; cursor: pointer; text-align: left;
  &:hover { color: ${({ theme }) => theme.color.point_y_100}; text-decoration: underline; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
`;
const TimeActionsPopup = styled.div`
  position: fixed; z-index: 500; box-sizing: border-box; width: 240px; padding: 10px;
  display: flex; flex-direction: column; gap: 8px;
  border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  background: ${({ theme }) => theme.color.primary_100}; color: ${({ theme }) => theme.color.text_color_100};
  box-shadow: 0 8px 24px ${({ theme }) => theme.name === 'dark' ? '#00000070' : '#10182824'};
  strong { font-size: 12px; font-weight: 600; }
  > div { display: flex; gap: 6px; }
`;
const DetailButton = styled(T.ActionButton)`&:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }`;
const IdActions = styled.div`display: flex; min-width: 0; flex-direction: column; align-items: flex-start; gap: 4px; code { font-size: 11px; overflow-wrap: anywhere; }`;
const IdTools = styled.div`display: flex; gap: 2px;`;
const IdAction = styled(T.ActionButton)`height: 24px; padding: 0 5px; font-size: 11px;`;
const PaginationFocus = styled.div`
  nav[aria-label='페이지 이동'] button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.point_y_100};
    outline-offset: 2px;
  }
`;
const Muted = styled.span`color: ${({ theme }) => theme.color.text_color_300};`;

const isUuid = (value: string) => /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(value);
const formatTimestamp = (value?: string) => {
  if (!value) return '—';
  const match = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);
  return match ? `${match[1]} ${match[2]}` : value;
};

const pad = (value: number) => String(value).padStart(2, '0');
const localDateTime = (date: Date) => `${getLocalDateInputValue(0, date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
const FULL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?$/;
const dateTimeValue = (value: string | null, fallback: string, end = false) => {
  if (!value) return fallback;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T${end ? '23:59:59' : '00:00:00'}`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return `${value}:00`;
  return FULL_DATE_TIME.test(value)
    ? value.replace(/\.(\d{3})\d+$/, '.$1') : fallback;
};

const readFilters = (params: URLSearchParams): Filters => ({
  userId: params.get('userId') || '',
  callId: params.get('callId') || '',
  clientCallId: params.get('clientCallId') || '',
  eventName: params.get('eventName') || '',
  category: params.get('category') || (params.get('view') === 'error' ? 'ERROR' : ''),
  source: params.get('source') || '',
  deliveryStatus: params.get('deliveryStatus') || '',
  receivedAtFrom: dateTimeValue(params.get('receivedAtFrom'), `${getLocalDateInputValue(params.get('view') === 'week' ? -6 : 0)}T00:00:00`),
  receivedAtTo: dateTimeValue(params.get('receivedAtTo'), `${getLocalDateInputValue()}T23:59:59`, true),
  limit: ['20', '50', '100'].includes(params.get('limit') || '') ? params.get('limit')! : '50',
});

const queryFromUrl = (searchParams: URLSearchParams) => {
  const filters = readFilters(searchParams);
  const numberParam = (key: string, fallback: number) => {
    const value = searchParams.get(key);
    const parsed = value ? Number(value) : NaN;
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
  };
  const rawLimit = Number(searchParams.get('limit'));
  const limit = [20, 50, 100].includes(rawLimit) ? rawLimit : 50;
  const orderAsc = searchParams.get('orderAsc') === 'ASC' ? 'ASC' : 'DESC';
  const params: Record<string, string | number> = {
    pg: numberParam('pg', 1), limit, orderAsc,
  };
  const userId = filters.userId.trim();
  if (userId && /^\d+$/.test(userId)) params.userId = Number(userId);
  for (const key of FILTER_KEYS.slice(1)) {
    const value = filters[key].trim();
    if (value && (key !== 'clientCallId' || isUuid(value))) params[key] = value;
  }
  const rawFrom = searchParams.get('receivedAtFrom');
  const rawTo = searchParams.get('receivedAtTo');
  const exactTime = searchParams.get('exactTime') === '1' && FULL_DATE_TIME.test(rawFrom || '') && FULL_DATE_TIME.test(rawTo || '');
  params.receivedAtFrom = exactTime ? rawFrom! : filters.receivedAtFrom;
  const end = exactTime ? rawTo! : filters.receivedAtTo;
  const fraction = end.match(/\.(\d+)$/)?.[1];
  params.receivedAtTo = exactTime || (fraction && /[1-9]/.test(fraction))
    ? end : `${end.replace(/\.0+$/, '')}.999999`;
  return params;
};

const prettyDetails = (value?: string) => {
  if (!value) return 'details 없음';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const valueText = (value: unknown) => typeof value === 'string' ? value : JSON.stringify(value);

const formatCurrentPageLogs = (rows: MobileCallLog[], query: Record<string, string | number>, page: PageData) => {
  const sections = rows.map((row, index) => {
    const record: Record<string, unknown> = { ...row };
    delete record.details;
    if (EVENT_LABELS[row.eventName]) record.eventLabel = EVENT_LABELS[row.eventName];
    if (row.details) {
      try { record.details_json = JSON.parse(row.details); }
      catch { record.details_json_raw = row.details; }
    }
    return [
      `## ${index + 1}. ${formatTimestamp(row.receivedAt)} | ${row.source} / ${row.category} / ${row.eventName.replace(/[\r\n]+/g, ' ')}`,
      '```json', JSON.stringify(record, null, 2), '```', '',
    ].join('\n');
  });

  return [
    '# Callis 모바일 로그 · 현재 페이지', '',
    `- 페이지: ${query.pg} / ${page.totalPages ?? 0}`,
    `- 현재 페이지: ${rows.length}건`,
    `- 검색 전체: ${page.totalElements ?? rows.length}건`,
    `- 정렬: receivedAt ${query.orderAsc}`,
    '- occurredAt은 기기 기록 시각, receivedAt은 서버 수집 시각입니다.',
    '- 누락된 필드는 추정하지 않습니다. ACCEPTED는 단말 수신이나 통화 응답의 증거가 아닙니다.',
    '', '## 조회 조건', '```json', JSON.stringify(query, null, 2), '```', '',
    ...sections,
  ].join('\n');
};

export default function MobileCallLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set());
  const [preview, setPreview] = useState<DetailsPreview | null>(null);
  const [timeActions, setTimeActions] = useState<TimeActions | null>(null);
  const timeHideTimer = useRef<number | undefined>(undefined);
  const timeTrigger = useRef<HTMLButtonElement | null>(null);
  const firstTimeAction = useRef<HTMLButtonElement | null>(null);
  const [filters, setFilters] = useState<Filters>(() => readFilters(searchParams));
  const [eventInputMode, setEventInputMode] = useState<'select' | 'direct'>(() => {
    const eventName = searchParams.get('eventName') || '';
    return eventName && !isKnownEvent(eventName) ? 'direct' : 'select';
  });
  const { data, loading, error, request } = useApi<PageData>();
  const query = useMemo(() => queryFromUrl(new URLSearchParams(searchKey)), [searchKey]);
  const rows = data?.content || [];
  const totalPages = data?.totalPages || 0;
  const hasAppliedFilter = FILTER_KEYS.some(key => Boolean(searchParams.get(key)?.trim())) || searchParams.has('receivedAtFrom') || searchParams.has('receivedAtTo');
  const view = searchParams.has('receivedAtFrom') || searchParams.has('receivedAtTo') ? 'custom' : searchParams.get('view') || 'day';
  const invalidClientCallId = Boolean(filters.clientCallId.trim() && !isUuid(filters.clientCallId.trim()));

  useEffect(() => {
    const nextFilters = readFilters(new URLSearchParams(searchKey));
    setFilters(nextFilters);
    if (nextFilters.eventName && !isKnownEvent(nextFilters.eventName)) setEventInputMode('direct');
    setPreview(null);
    setTimeActions(null);
  }, [searchKey]);

  useEffect(() => () => window.clearTimeout(timeHideTimer.current), []);
  useEffect(() => {
    if (timeActions?.focusActions) firstTimeAction.current?.focus();
  }, [timeActions]);

  useEffect(() => {
    void request({ url: '/call/mobile-logs', method: 'GET', params: query }).catch(() => {});
  }, [request, searchKey, query]);

  const storeFilters = (nextFilters: Filters, exactTime = false) => {
    setFilters(nextFilters);
    const clientCallId = nextFilters.clientCallId.trim();
    if (clientCallId && !isUuid(clientCallId)) return;
    const previousFrom = searchParams.get('receivedAtFrom');
    const previousTo = searchParams.get('receivedAtTo');
    const preserveExactTime = !exactTime && searchParams.get('exactTime') === '1' &&
      nextFilters.receivedAtFrom === dateTimeValue(previousFrom, '') &&
      nextFilters.receivedAtTo === dateTimeValue(previousTo, '', true);
    const next = new URLSearchParams();
    next.set('pg', '1');
    next.set('limit', nextFilters.limit);
    next.set('orderAsc', searchParams.get('orderAsc') === 'ASC' ? 'ASC' : 'DESC');
    for (const key of FILTER_KEYS) {
      const value = nextFilters[key].trim();
      if (value) next.set(key, value);
    }
    next.set('receivedAtFrom', preserveExactTime ? previousFrom! : nextFilters.receivedAtFrom.trim());
    next.set('receivedAtTo', preserveExactTime ? previousTo! : nextFilters.receivedAtTo.trim());
    if (exactTime || preserveExactTime) next.set('exactTime', '1');
    if (next.toString() === searchKey) void retry();
    else setSearchParams(next);
  };

  const changePage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('pg', String(page));
    setSearchParams(next);
  };

  const changeLimit = (limit: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('pg', '1');
    next.set('limit', limit);
    setSearchParams(next);
  };

  const retry = () => {
    setPreview(null);
    setTimeActions(null);
    if (view !== 'custom') setFilters(readFilters(new URLSearchParams(searchKey)));
    return request({ url: '/call/mobile-logs', method: 'GET', params: queryFromUrl(new URLSearchParams(searchKey)) }).catch(() => {});
  };
  const refresh = () => void retry();
  const selectView = (nextView: 'day' | 'week' | 'error') => {
    const next = new URLSearchParams();
    next.set('view', nextView);
    next.set('pg', '1');
    next.set('limit', filters.limit);
    next.set('orderAsc', searchParams.get('orderAsc') === 'ASC' ? 'ASC' : 'DESC');
    if (nextView === 'error') next.set('category', 'ERROR');
    setSearchParams(next);
  };
  const resetFilters = () => {
    setFilters(readFilters(new URLSearchParams()));
    setEventInputMode('select');
    setSearchParams(new URLSearchParams());
  };
  const changeEventInputMode = (mode: 'select' | 'direct') => {
    setEventInputMode(mode);
    if (mode === 'select' && filters.eventName && !isKnownEvent(filters.eventName)) {
      setFilters({ ...filters, eventName: '' });
    }
  };
  const searchById = (key: 'callId' | 'clientCallId', value: string) => storeFilters({
    ...filters,
    callId: key === 'callId' ? value : '',
    clientCallId: key === 'clientCallId' ? value : '',
  });
  const copyId = async (key: 'callId' | 'clientCallId', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${key}를 복사했습니다.`);
    } catch {
      toast.error('클립보드에 복사하지 못했습니다.');
    }
  };
  const copyCurrentPageLogs = async () => {
    try {
      await navigator.clipboard.writeText(formatCurrentPageLogs(rows, query, data || {}));
      toast.success(`현재 페이지 로그 ${rows.length}건을 복사했습니다.`);
    } catch {
      toast.error('현재 페이지 로그를 복사하지 못했습니다.');
    }
  };
  const showPreview = (row: MobileCallLog, button: HTMLButtonElement) => {
    const rect = button.getBoundingClientRect();
    const above = rect.top > window.innerHeight - rect.bottom;
    const availableHeight = above ? rect.top - 16 : window.innerHeight - rect.bottom - 16;
    const width = Math.min(420, window.innerWidth - 32);
    setPreview({
      id: row.id,
      text: prettyDetails(row.details),
      left: Math.max(16, Math.min(rect.right - width, window.innerWidth - width - 16)),
      top: above ? rect.top - 8 : rect.bottom + 8,
      width,
      maxHeight: Math.max(80, Math.min(440, availableHeight)),
      above,
    });
  };
  const closeTimeActions = () => {
    window.clearTimeout(timeHideTimer.current);
    setTimeActions(null);
  };
  const scheduleTimeActionsClose = () => {
    window.clearTimeout(timeHideTimer.current);
    timeHideTimer.current = window.setTimeout(() => setTimeActions(null), 150);
  };
  const showTimeActions = (row: MobileCallLog, button: HTMLButtonElement, focusActions = false) => {
    window.clearTimeout(timeHideTimer.current);
    timeTrigger.current = button;
    const rect = button.getBoundingClientRect();
    const above = rect.top > window.innerHeight - rect.bottom;
    setTimeActions({
      id: row.id, receivedAt: row.receivedAt,
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 256)),
      top: above ? rect.top - 8 : rect.bottom + 8,
      above, focusActions,
    });
  };
  const searchAroundTime = (receivedAt: string, minutes: number) => {
    const match = receivedAt.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(\.\d+)?/);
    if (!match) { toast.error('수집 시각을 해석할 수 없습니다.'); return; }
    const base = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6]));
    if (Number.isNaN(base.getTime())) { toast.error('수집 시각을 해석할 수 없습니다.'); return; }
    const offset = minutes * 60 * 1000;
    const fraction = match[7] || '';
    closeTimeActions();
    storeFilters({
      ...readFilters(new URLSearchParams()), limit: filters.limit,
      receivedAtFrom: localDateTime(new Date(base.getTime() - offset)) + fraction,
      receivedAtTo: localDateTime(new Date(base.getTime() + offset)) + fraction,
    }, true);
  };
  const toggleDetails = (id: number) => setExpandedRows(current => {
    const next = new Set(current);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return <PageContainer>
    <PageHeader
      title="모바일 로그 조회"
      count={!loading && !error && typeof data?.totalElements === 'number' ? data.totalElements : undefined}
      description="기본은 오늘 00:00~23:59:59입니다. 기간은 서버 수집 시각(receivedAt) 기준입니다."
      actions={<Button $variant="secondary" onClick={refresh} disabled={loading}>새로고침</Button>}
    />

    <FiltersPanel onSubmit={event => { event.preventDefault(); storeFilters(filters); }}>
      <QuickFilters aria-label="빠른 필터">
        <Button type="button" $variant={view === 'day' ? 'primary' : 'secondary'} aria-pressed={view === 'day'} onClick={() => selectView('day')}>1일치</Button>
        <Button type="button" $variant={view === 'week' ? 'primary' : 'secondary'} aria-pressed={view === 'week'} onClick={() => selectView('week')}>최근 7일</Button>
        <Button type="button" $variant={view === 'error' ? 'primary' : 'secondary'} aria-pressed={view === 'error'} onClick={() => selectView('error')}>오류만</Button>
      </QuickFilters>

      <FilterGrid>
        <Field $width={240}><Label htmlFor="receivedAtFrom">수집 시작 시각</Label><Input id="receivedAtFrom" type="datetime-local" step="any" required max={filters.receivedAtTo} title={searchParams.get('exactTime') === '1' ? searchParams.get('receivedAtFrom') || '' : undefined} value={filters.receivedAtFrom} onChange={event => setFilters({ ...filters, receivedAtFrom: event.target.value })} /></Field>
        <Field $width={240}><Label htmlFor="receivedAtTo">수집 종료 시각</Label><Input id="receivedAtTo" type="datetime-local" step="any" required min={filters.receivedAtFrom} title={searchParams.get('exactTime') === '1' ? searchParams.get('receivedAtTo') || '' : undefined} value={filters.receivedAtTo} onChange={event => setFilters({ ...filters, receivedAtTo: event.target.value })} /></Field>
        <Field><Label htmlFor="category">분류</Label><Select id="category" value={filters.category} onChange={event => setFilters({ ...filters, category: event.target.value })}><option value="">전체</option>{CATEGORIES.map(value => <option key={value} value={value}>{value}</option>)}</Select></Field>
        <Field><Label htmlFor="source">출처</Label><Select id="source" value={filters.source} onChange={event => setFilters({ ...filters, source: event.target.value })}><option value="">전체</option>{SOURCES.map(value => <option key={value} value={value}>{value}</option>)}</Select></Field>
        <Field><Label htmlFor="deliveryStatus">Push 상태</Label><Select id="deliveryStatus" value={filters.deliveryStatus} onChange={event => setFilters({ ...filters, deliveryStatus: event.target.value })}><option value="">전체</option>{DELIVERY_STATUSES.map(value => <option key={value} value={value}>{value}</option>)}</Select></Field>
        <Field $width={110}><Label htmlFor="userId">사용자 ID</Label><Input id="userId" type="number" min="1" step="1" value={filters.userId} onChange={event => setFilters({ ...filters, userId: event.target.value })} /></Field>
        <Field $width={140}><Label htmlFor="callId">통화 ID</Label><Input id="callId" value={filters.callId} onChange={event => setFilters({ ...filters, callId: event.target.value })} /></Field>
        <Field>
          <Label htmlFor="clientCallId">클라이언트 통화 ID</Label>
          <Input id="clientCallId" value={filters.clientCallId} aria-invalid={invalidClientCallId || undefined} aria-describedby={invalidClientCallId ? 'clientCallId-error' : undefined} onChange={event => setFilters({ ...filters, clientCallId: event.target.value })} />
          {invalidClientCallId && <FieldError id="clientCallId-error" role="alert">UUID 형식의 값을 입력해 주세요.</FieldError>}
        </Field>
        <Field $width={280}>
          <Label htmlFor="eventName">이벤트명 완전 일치</Label>
          <EventControl>
            <EventModeTabs role="group" aria-label="이벤트명 입력 방식">
              <EventModeButton type="button" aria-pressed={eventInputMode === 'select'} onClick={() => changeEventInputMode('select')}>선택</EventModeButton>
              <EventModeButton type="button" aria-pressed={eventInputMode === 'direct'} onClick={() => changeEventInputMode('direct')}>직접</EventModeButton>
            </EventModeTabs>
            {eventInputMode === 'select' ? (
              <Select id="eventName" value={filters.eventName} onChange={event => setFilters({ ...filters, eventName: event.target.value })}>
                <option value="">전체 이벤트</option>
                {EVENT_OPTIONS.map(([name, label]) => <option key={name} value={name}>{label} ({name})</option>)}
              </Select>
            ) : <Input id="eventName" value={filters.eventName} onChange={event => setFilters({ ...filters, eventName: event.target.value })} />}
          </EventControl>
        </Field>
        <Actions>
          <Field $width={120}>
            <Label htmlFor="limit">페이지당 로그 수</Label>
            <Select id="limit" value={filters.limit} onChange={event => changeLimit(event.target.value)}>
              {[20, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}
            </Select>
          </Field>
          <Button type="submit">조회</Button>
          <Button type="button" $variant="secondary" onClick={resetFilters}>초기화</Button>
        </Actions>
      </FilterGrid>
    </FiltersPanel>

    <Note role="note">Push 상태는 서버의 발송 기록입니다. ACCEPTED는 단말 수신이나 통화 응답을 확인하지 않습니다. FCM·PUSHKIT과 모바일 앱 로그는 각각 별도 행으로 표시합니다.</Note>

    {loading || (!data && !error) ? <PageState status="loading" /> : error ? <PageState status="error" onRetry={retry} /> : rows.length === 0 ? <PageState status="empty" title={hasAppliedFilter ? '조건에 맞는 로그가 없습니다.' : view === 'week' ? '최근 7일 동안 로그가 없습니다.' : '오늘 조회된 로그가 없습니다.'} /> : <>
      <Results>
      <TableTools><Button type="button" $variant="secondary" $size="sm" onClick={() => void copyCurrentPageLogs()}>현재 페이지 로그 복사</Button></TableTools>
      <TableArea>
        <T.Table aria-label="모바일 로그 결과">
          <thead><tr>
            <T.Th $width="145px">발생 시각 (occurredAt)</T.Th><T.Th $width="145px">수집 시각 (receivedAt)</T.Th><T.Th $width="80px">사용자 ID</T.Th>
            <T.Th $width="130px">출처</T.Th><T.Th $width="190px">분류 / 이벤트</T.Th><T.Th $width="125px">Push 상태</T.Th>
            <T.Th $width="125px">통화 ID</T.Th><T.Th $width="180px">클라이언트 통화 ID</T.Th><T.Th $width="82px">상세</T.Th>
          </tr></thead>
          <tbody>{rows.map(row => {
            const expanded = expandedRows.has(row.id);
            const formattedDetails = expanded ? prettyDetails(row.details) : '';
            return <React.Fragment key={row.id}>
            <T.Tr>
              <Cell title={row.occurredAt || ''}>{formatTimestamp(row.occurredAt)}</Cell>
              <Cell title={row.receivedAt}><TimeTrigger
                type="button"
                aria-label={`수집 시각 ${formatTimestamp(row.receivedAt)} 주변 조회`}
                aria-expanded={timeActions?.id === row.id}
                aria-controls={timeActions?.id === row.id ? 'mobile-log-time-actions' : undefined}
                onMouseEnter={event => showTimeActions(row, event.currentTarget)}
                onMouseLeave={scheduleTimeActionsClose}
                onFocus={event => showTimeActions(row, event.currentTarget)}
                onBlur={scheduleTimeActionsClose}
                onClick={event => showTimeActions(row, event.currentTarget, true)}
              >{formatTimestamp(row.receivedAt)}</TimeTrigger></Cell>
              <Cell>{row.userId}</Cell>
              <Cell><T.Badge $tone={row.source === 'MOBILE_CLIENT' ? 'neutral' : 'info'}>{row.source === 'MOBILE_CLIENT' ? '앱 (MOBILE_CLIENT)' : row.source}</T.Badge></Cell>
              <Cell><CategoryBadge $category={row.category} $tone={CATEGORY_TONES[row.category] || 'neutral'}>{row.category}</CategoryBadge><br /><span title={row.eventName}>{EVENT_LABELS[row.eventName] || row.eventName} <code>{row.eventName}</code></span></Cell>
              <Cell aria-label={`Push 상태 ${row.deliveryStatus || '없음'}`} title="ACCEPTED는 단말 수신이나 통화 응답을 뜻하지 않습니다.">{row.deliveryStatus ? <T.Badge $tone={row.deliveryStatus === 'FAILED' || row.deliveryStatus === 'DISPATCH_REJECTED' ? 'danger' : 'neutral'}>{row.deliveryStatus}</T.Badge> : '—'}</Cell>
              <Cell title={row.callId || ''}>{row.callId ? <IdActions><code>{row.callId}</code><IdTools>
                <IdAction aria-label={`callId 검색 ${row.callId}`} onClick={() => searchById('callId', row.callId!)}>검색</IdAction>
                <IdAction aria-label={`callId 복사 ${row.callId}`} onClick={() => void copyId('callId', row.callId!)}>복사</IdAction>
              </IdTools></IdActions> : '—'}</Cell>
              <Cell title={row.clientCallId || ''}>{row.clientCallId ? <IdActions><code>{row.clientCallId}</code><IdTools>
                <IdAction aria-label={`clientCallId 검색 ${row.clientCallId}`} onClick={() => searchById('clientCallId', row.clientCallId!)}>검색</IdAction>
                <IdAction aria-label={`clientCallId 복사 ${row.clientCallId}`} onClick={() => void copyId('clientCallId', row.clientCallId!)}>복사</IdAction>
              </IdTools></IdActions> : '—'}</Cell>
              <Cell><DetailButton
                aria-expanded={expanded}
                aria-label={`${expanded ? '상세 닫기' : '상세 보기'} ${row.eventName}`}
                aria-describedby={preview?.id === row.id ? 'mobile-log-details-preview' : undefined}
                onMouseEnter={event => showPreview(row, event.currentTarget)}
                onMouseLeave={() => setPreview(null)}
                onFocus={event => showPreview(row, event.currentTarget)}
                onBlur={() => setPreview(null)}
                onClick={() => { setPreview(null); toggleDetails(row.id); }}
              >{expanded ? '닫기' : '보기'}</DetailButton></Cell>
            </T.Tr>
            {expanded && <T.Tr><td colSpan={9}><InlineDetails role="region" aria-label="로그 상세">
              <strong>{EVENT_LABELS[row.eventName] || row.eventName} <code>{row.eventName}</code></strong>
              <strong>로그 메타데이터</strong>
              <DetailGrid>{[
                ['id', row.id], ['userId', row.userId], ['source', row.source], ['category', row.category],
                ['callId', row.callId], ['clientCallId', row.clientCallId], ['이벤트 ID (clientEventId)', row.clientEventId],
                ['occurredAt', row.occurredAt], ['receivedAt', row.receivedAt], ['deliveryStatus', row.deliveryStatus],
              ].map(([key, value]) => <React.Fragment key={key as string}><dt>{key}</dt><dd>{value === undefined || value === '' ? '—' : valueText(value)}</dd></React.Fragment>)}</DetailGrid>
              {row.message && <p>{row.message}</p>}
              {row.details && <><strong>details_json</strong><PrettyJson aria-label="details_json">{formattedDetails}</PrettyJson></>}
              {!row.details && <Muted>details 없음</Muted>}
              <Note>발생 시각과 수집 시각은 서로 다른 기록입니다. 이벤트 간 순서를 비교할 때 기기 시계 차이를 고려하세요.</Note>
            </InlineDetails></td></T.Tr>}
            </React.Fragment>;
          })}</tbody>
        </T.Table>
      </TableArea>
      </Results>
      <T.TableFooter>
        <PaginationFocus><Pagination currentPage={Number(query.pg)} totalPages={totalPages} onPageChange={changePage} /></PaginationFocus>
      </T.TableFooter>
    </>}
    {preview && createPortal(<DetailsTooltip
      id="mobile-log-details-preview"
      role="tooltip"
      style={{ left: preview.left, top: preview.top, width: preview.width, maxHeight: preview.maxHeight, transform: preview.above ? 'translateY(-100%)' : undefined }}
    >{preview.text}</DetailsTooltip>, document.body)}
    {timeActions && createPortal(<TimeActionsPopup
      id="mobile-log-time-actions"
      role="group"
      aria-label="수집 시각 주변 조회"
      style={{ left: timeActions.left, top: timeActions.top, transform: timeActions.above ? 'translateY(-100%)' : undefined }}
      onMouseEnter={() => window.clearTimeout(timeHideTimer.current)}
      onMouseLeave={closeTimeActions}
      onFocus={() => window.clearTimeout(timeHideTimer.current)}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) closeTimeActions(); }}
      onKeyDown={event => { if (event.key === 'Escape') { closeTimeActions(); timeTrigger.current?.focus(); } }}
    >
      <strong>기준 {timeActions.receivedAt.replace('T', ' ')}</strong>
      <div>
        <Button type="button" $variant="secondary" $size="sm" ref={firstTimeAction} onClick={() => searchAroundTime(timeActions.receivedAt, 30)}>±30분 조회</Button>
        <Button type="button" $variant="secondary" $size="sm" onClick={() => searchAroundTime(timeActions.receivedAt, 60)}>±1시간 조회</Button>
      </div>
    </TimeActionsPopup>, document.body)}
  </PageContainer>;
}
